import React from 'react';
import { Badge, Button, ButtonGroup, Checkbox, Content, Dialog, Divider, Flex, Heading, Item, NumberField, Picker, useDialogContainer, } from '@adobe/react-spectrum';
import { ColorsEditor, ColorsReset, } from 'app/views/components/dialogs/settings/Colors';
import { Text } from 'app/views/components/typography/Text';
import { analytics } from 'app/utils/analytics';
import { hasLocalStorage } from 'app/utils/caniuse';
import { Capabilities } from 'app/utils/Capabilities';
import { settings } from 'app/utils/settingsStorage';
const getPlatformSpecificShort = (combination) => combination
    .replace('meta', Capabilities.shared.isMac ? 'cmd' : 'ctrl')
    .replaceAll('+', ' + ');
export const SettingsDialog = () => {
    const dialog = useDialogContainer();
    const setConfigValue = (key, value) => {
        // record changes
        analytics({
            action: 'settings:set',
            label: `${key}:${value}`,
        });
        // set config
        configSet(key, value);
        // set changed value in LC
        settings().set(key, value);
    };
    return (React.createElement(Dialog, null,
        React.createElement(Heading, null, "Application settings"),
        React.createElement(Divider, null),
        React.createElement(Content, null,
            !hasLocalStorage() && (React.createElement(Flex, { alignItems: "center", direction: "column" },
                React.createElement(Text, { size: "S", color: "red-400" }, "Due to security restrictions settings will not be saved"),
                React.createElement(Divider, { orientation: "horizontal", size: "S", marginY: "size-150" }))),
            React.createElement(Flex, { direction: "column", gap: "size-150" },
                React.createElement(Flex, { direction: "row", gap: "size-100", alignItems: "center" },
                    React.createElement(Text, { size: "M" }, "Application toolbar location"),
                    React.createElement(Picker, { selectedKey: configGet('desktopMainViewLayout', 'row'), onSelectionChange: key => {
                            setConfigValue('desktopMainViewLayout', key);
                        }, "aria-label": "Sidebar location picker" },
                        React.createElement(Item, { key: "row" }, "Left"),
                        React.createElement(Item, { key: "column" }, "Top"))),
                React.createElement(Flex, { direction: "row", gap: "size-100", alignItems: "center" },
                    React.createElement(Text, { size: "M" }, "Copy to clipboard shortcut"),
                    React.createElement(Picker, { selectedKey: configGet('shortcutsCopyToClipboard', 'meta+c'), onSelectionChange: key => {
                            setConfigValue('shortcutsCopyToClipboard', key);
                        }, "aria-label": "Sidebar location picker" },
                        React.createElement(Item, { key: "meta+c" }, getPlatformSpecificShort('meta+c')),
                        React.createElement(Item, { key: "meta+shift+c" }, getPlatformSpecificShort('meta+shift+c')))),
                React.createElement(Flex, { direction: "row", gap: "size-100", alignItems: "center" },
                    React.createElement(Text, { size: "M" }, "Screen capture delay (sec.)"),
                    React.createElement(NumberField, { minValue: 0, maxValue: 20, step: 0.5, value: configGet('screenCaptureDelay', 2000) / 1000, onChange: value => {
                            setConfigValue('screenCaptureDelay', Math.trunc(value * 1000));
                        } })),
                React.createElement(Divider, { orientation: "horizontal", size: "S" }),
                React.createElement(Text, { size: "M" }, "Application settings"),
                React.createElement(Flex, { direction: "column" },
                    React.createElement(Checkbox, { isSelected: configGet('hideWatermarkDuringRollout', false), onChange: value => {
                            setConfigValue('hideWatermarkDuringRollout', value);
                        } },
                        "Remove Annotely watermark",
                        ' ',
                        React.createElement(Badge, { variant: "positive" }, "Limited offer")),
                    React.createElement(Checkbox, { isSelected: configGet('warnBeforeUnload', true), onChange: value => {
                            setConfigValue('warnBeforeUnload', value);
                        } }, "Always warn before closing window"),
                    React.createElement(Checkbox, { isSelected: configGet('addSlidesOnImageDrop', false), onChange: value => {
                            setConfigValue('addSlidesOnImageDrop', value);
                        } }, "Always add slides when using drag & drop"),
                    React.createElement(Checkbox, { isSelected: configGet('allowToolDeselection', true), onChange: value => {
                            setConfigValue('allowToolDeselection', value);
                        } }, "Enable deselecting active tool in toolbar")),
                React.createElement(Divider, { orientation: "horizontal", size: "S" }),
                React.createElement(Text, { size: "M" }, "Markers settings"),
                React.createElement(Flex, { direction: "column" },
                    React.createElement(Checkbox, { isSelected: configGet('renumeratePinsAfterDeletion', true), onChange: value => {
                            setConfigValue('renumeratePinsAfterDeletion', value);
                        } }, "Update pin numbers when pin is deleted"),
                    React.createElement(Checkbox, { isSelected: configGet('autolockBlurs', true), onChange: value => {
                            setConfigValue('autolockBlurs', value);
                        } }, "Auto lock blurs on addition"),
                    React.createElement(Checkbox, { isSelected: configGet('pinNoteAnchor', true), onChange: value => {
                            setConfigValue('pinNoteAnchor', value);
                        } }, "Pin note anchor on drag"),
                    React.createElement(Checkbox, { isSelected: configGet('saveColorPerTool', true), onChange: value => {
                            setConfigValue('saveColorPerTool', value);
                        } }, "Save color selected for tool"),
                    React.createElement(Checkbox, { isSelected: configGet('useWhiteStroke', true), onChange: value => {
                            setConfigValue('useWhiteStroke', value);
                        } }, "Use white stroke"),
                    React.createElement(Checkbox, { isSelected: configGet('smartScalingMarkers', true), onChange: value => {
                            setConfigValue('smartScalingMarkers', value);
                        } }, "Scale markers to image display size")),
                React.createElement(Divider, { orientation: "horizontal", size: "S" }),
                React.createElement(Text, { size: "M" }, "Slides settings"),
                React.createElement(Flex, { direction: "column" },
                    React.createElement(Checkbox, { isSelected: configGet('slideDuplicateKeepItems', false), onChange: value => {
                            setConfigValue('slideDuplicateKeepItems', value);
                        } }, "Keep all markers on slide duplication")),
                React.createElement(Divider, { orientation: "horizontal", size: "S" }),
                React.createElement(Flex, { direction: "column", gap: "size-100", alignItems: "stretch", justifyContent: "center" },
                    React.createElement(Flex, { direction: "row" },
                        React.createElement(Text, { size: "M", style: { flex: 1 } }, "Default colors"),
                        React.createElement(ColorsReset, null)),
                    React.createElement(ColorsEditor, null)),
                React.createElement(Divider, { orientation: "horizontal", size: "S" }))),
        React.createElement(ButtonGroup, null,
            React.createElement(Button, { variant: "secondary", onPress: dialog.dismiss }, "Close"))));
};
